import React, { Suspense, lazy, useEffect, useState, Component, ReactNode } from 'react'
import './App.css'
import { ThemeProvider } from './contexts/ThemeContext'
import { initMooSend, loadPopupForm } from './utils/moosend'
import CookieConsent from './components/CookieConsent'
import PWAInstallPrompt from './components/PWAInstallPrompt'

// Lazy load components for better performance
const Nav = lazy(() => import('./components/nav/Nav'))
const Home = lazy(() => import('./components/home/Home'))
const About = lazy(() => import('./components/about/About'))
const Skills = lazy(() => import('./components/skills/Skills'))
const Qualification = lazy(() => import('./components/qualification/Qualification'))
const Services = lazy(() => import('./components/services/Services'))
const Blog = lazy(() => import('./components/blog/Blog'))
const Contact = lazy(() => import('./components/contact/Contact'))
const Footer = lazy(() => import('./components/footer/Footer'))
const ScrollUp = lazy(() => import('./components/scrollup/ScrollUp'))

// Enhanced Loading Component
const LoadingSpinner: React.FC = () => (
  <div className="loading-container" role="status" aria-label="Loading content">
    <div className="loading-spinner">
      <div className="spinner" aria-hidden="true"></div>
      <p>Loading...</p>
      <div className="loading-progress">
        <div className="progress-bar"></div>
      </div>
    </div>
  </div>
)

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <h2>Something went wrong</h2>
          <p>Please refresh the page to try again.</p>
          <button onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    // Initialize MooSend
    initMooSend();

    // Wait for MooSend to initialize before loading popup form
    const interval = setInterval(() => {
      if ((window as any).mootrack) {
        loadPopupForm();
        clearInterval(interval);
      }
    }, 1000);

    // Cleanup interval
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 200)
    return () => clearTimeout(timer)
  }, [])

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <div className="App">
          {/* Skip to content link for accessibility */}
          <a href="#main-content" className="skip-link">
            Skip to main content
          </a>
          
          <Suspense fallback={<LoadingSpinner />}>
            <Nav />
            <main id="main-content" className="main">
              <Home />
              <About />
              <Skills />
              <Qualification />
              <Services />
              {/*<Portfolio />*/}
              <Blog />
              {/*<Testimonials />*/}
              <Contact />
            </main>
            <Footer />
            <ScrollUp />
          </Suspense>
          <CookieConsent />
          <PWAInstallPrompt />
        </div>
      </ErrorBoundary>
    </ThemeProvider>
  )
}

export default App 